import { useState, useRef, useEffect, useCallback } from 'react';

// ピンのデザインを共通化
const PinFace = ({ faded = false }) => (
  <div
    className="flex flex-col items-center justify-center rounded-full shadow-lg"
    style={{
      width: 70,
      height: 70,
      background: '#f59e0b',
      opacity: faded ? 0.3 : 1,
    }}
  >
    <img
      src="/images/footprint-pin.svg"
      alt="足あと"
      style={{
        width: 28,
        height: 28,
        filter: 'brightness(0) invert(1)', // SVGを白色に
      }}
    />
    <span style={{ color: '#fff', fontSize: 12, fontWeight: 'bold', lineHeight: 1, marginTop: 4}}>
      足あと
    </span>
  </div>
);

/**
 * 足あとボタンの使い方ツールチップ
 * ドラッグ前のみ表示される吹き出し
 */
const FootprintTooltip = () => (
  <div className="absolute bottom-8 left-3 z-48 pointer-events-none">
    {/* 吹き出し */}
    <div className="bg-white rounded-2xl shadow-2xl border-2 border-amber-400 p-4 w-64">
      {/* 矢印（上向き） */}
      <div
        className="absolute -top-2 left-8 w-4 h-4 bg-white border-l-2 border-t-2 border-amber-400"
        style={{
          transform: 'rotate(45deg)',
        }}
      />
      {/* テキスト */}
      <p className="text-sm font-bold text-gray-800 leading-relaxed">
        マップを拡大して、
        <br/>
        足あとボタンをつかんで、
        <br/>
        訪れた場所に置いてみよう。
      </p>
    </div>
  </div>
);

/**
 * DraggablePostPin
 *
 * マップ左下に固定されたピン。
 * ドラッグ中はカーソルに追従して視覚的に動き、
 * 離した場所の緯度経度を onDrop() で返す。
 */
const DraggablePostPin = ({ mapRef, onDrop, onHomeClick }) => {
  const [dragPos, setDragPos] = useState(null);
  const isDragging = useRef(false);
  const mapSnapshot = useRef(null);

  const handlePointerDown = useCallback((e) => {
    if (!mapRef.current) return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    isDragging.current = true;
    mapSnapshot.current = {
      center: mapRef.current.getCenter(),
      zoom: mapRef.current.getZoom(),
    };
    setDragPos({ x: clientX, y: clientY });
  }, [mapRef]);

  const handlePointerMove = useCallback((e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragPos({ x: clientX, y: clientY });
  }, []);

  const handlePointerUp = useCallback((e) => {
    if (!isDragging.current || !mapSnapshot.current || !mapRef.current) return;
    const clientX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const clientY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
    isDragging.current = false;
    setDragPos(null);

    const mapDiv = mapRef.current.getDiv();
    const mapRect = mapDiv.getBoundingClientRect();
    const mapX = clientX - mapRect.left;
    const mapY = clientY - mapRect.top;

    const projection = mapRef.current.getProjection();
    if (!projection) return;

    const scale = Math.pow(2, mapSnapshot.current.zoom);
    const centerPoint = projection.fromLatLngToPoint(mapSnapshot.current.center);
    const dropPoint = new window.google.maps.Point(
      centerPoint.x + (mapX - mapRect.width / 2) / scale,
      centerPoint.y + (mapY - mapRect.height / 2) / scale,
    );
    const latLng = projection.fromPointToLatLng(dropPoint);
    onDrop({ lat: latLng.lat(), lng: latLng.lng() });
    mapSnapshot.current = null;
  }, [mapRef, onDrop]);

  useEffect(() => {
    if (dragPos === null) return;
    document.addEventListener('mousemove', handlePointerMove);
    document.addEventListener('mouseup', handlePointerUp);
    document.addEventListener('touchmove', handlePointerMove, { passive: false });
    document.addEventListener('touchend', handlePointerUp);
    return () => {
      document.removeEventListener('mousemove', handlePointerMove);
      document.removeEventListener('mouseup', handlePointerUp);
      document.removeEventListener('touchmove', handlePointerMove);
      document.removeEventListener('touchend', handlePointerUp);
    };
  }, [dragPos, handlePointerMove, handlePointerUp]);

  return (
    <>
      {/* ドラッグ中：カーソルに追従するゴースト（背景ごと移動） */}
      {dragPos && (
        <div
          style={{
            position: 'fixed',
            left: dragPos.x - 28,
            top: dragPos.y - 28,
            zIndex: 51,
            pointerEvents: 'none',
            transform: 'scale(1.15)',
            filter: 'drop-shadow(0 6px 8px rgba(0,0,0,0.5))',
          }}
        >
          <PinFace />
        </div>
      )}

      {/* 使い方ツールチップ（ドラッグ前のみ表示） */}
      {!dragPos && <FootprintTooltip />}
      {!dragPos && onHomeClick && (
        <button
          onClick={onHomeClick}
          className="absolute left-4 pointer-events-auto flex flex-col items-center justify-center gap-1 rounded-full shadow-lg bg-gray-400 active:bg-gray-300"
          style={{ bottom: '14.5rem', width: 70, height: 70, zIndex: 49 }}
          aria-label="最初の位置に戻る"
        >
          <img src="/images/home-map.svg" alt="" style={{ width: 28, height: 28, filter: 'brightness(0) invert(1)' }} />
          <span style={{ fontSize: 9, fontWeight: 'bold', color: '#ffffffff', lineHeight: 1 }}>最初に戻る</span>
        </button>
      )}

      {/* 固定ピン（左下） */}
      <div
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
        className="absolute bottom-36 left-4 pointer-events-auto"
        style={{ zIndex: 49, cursor: dragPos ? 'grabbing' : 'grab' }}
        title="ドラッグして足あとを置く場所を指定"
        aria-label="足あとを置く"
      >
        <PinFace faded={!!dragPos} />
      </div>
    </>
  );
};

export default DraggablePostPin;